import { FC, JSX, MouseEvent } from 'react';
import {
  Select as JoySelect,
  Option,
  FormControl,
  FormLabel,
  FormHelperText,
} from '@mui/joy';
import CloseIcon from '@mui/icons-material/Close';
import { SelectProps as JoySelectProps } from '@mui/joy';
import { useTranslation } from 'react-i18next';

export interface OptionType {
  label: string;
  value: string;
}

export interface DropdownProps extends JoySelectProps<any, any, any> {
  label?: string;
  error?: string;
  helperText?: string;
  options: OptionType[];
  size?: 'sm' | 'md' | 'lg';
  multiple?: boolean;
}

export const Dropdown: FC<DropdownProps> = ({
  label,
  error,
  helperText,
  options,
  size = 'sm',
  multiple = false,
  value,
  onChange,
  required,
  ...rest
}): JSX.Element => {
  const { t } = useTranslation();
  const selectedValues = Array.isArray(value) ? value.map(String) : [];

  const handleUnselect = (
    valToRemove: string,
    event: MouseEvent<SVGSVGElement>
  ) => {
    event.stopPropagation();
    if (onChange) {
      const updated = selectedValues.filter((val) => val !== valToRemove);
      onChange(null, updated);
    }
  };

  return (
    // `required` on the FormControl makes Joy render the asterisk on the label.
    <FormControl error={!!error} required={required} className="mb-1">
      {label && <FormLabel>{label}</FormLabel>}

      <JoySelect
        {...rest}
        required={required}
        multiple={multiple}
        value={value}
        onChange={onChange}
        placeholder={t('Select option')}
        size={size}
        slotProps={{
          listbox: {
            sx: {
              maxHeight: 300,
              zIndex: 10000,
              position: 'absolute',
            },
          },
        }}
      >
        {options?.map((option) => {
          const isSelected = selectedValues.includes(option.value);

          return (
            <Option
              key={option.value}
              value={option.value}
              onClick={(e) => {
                if (isSelected) e.preventDefault();
              }}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>{option.label}</span>
              {isSelected && (
                <CloseIcon
                  sx={{ cursor: 'pointer' }}
                  onClick={(e) => handleUnselect(option.value, e)}
                />
              )}
            </Option>
          );
        })}
      </JoySelect>

      <FormHelperText>{error ? error : helperText}</FormHelperText>
    </FormControl>
  );
};
